import { useCallback, useEffect, useRef, useState } from 'react';
import { DASHBOARD_ENDPOINTS, DASHBOARD_ERROR_KINDS, fetchDashboardData } from '../../services/dashboardApi.js';
import { CHART_CARD_STATUS } from '../../components/ChartCard.js';

/** 部品ごとの取得の状態（グラフのカードにそのまま渡すため、カードの状態と同じものを使う） */
export const SECTION_STATUS = CHART_CARD_STATUS;

const ENDPOINT_LIST = Object.values(DASHBOARD_ENDPOINTS);

const initialSections = () =>
    Object.fromEntries(ENDPOINT_LIST.map((ep) => [ep, { status: SECTION_STATUS.LOADING, data: null }]));

const toSection = (result) => {
    if (result.ok) return { status: SECTION_STATUS.READY, data: result.data };
    const status = result.errorKind === DASHBOARD_ERROR_KINDS.FORBIDDEN
        ? SECTION_STATUS.FORBIDDEN
        : SECTION_STATUS.ERROR;
    return { status, data: null };
};

/**
 * ダッシュボードの 5 本の API を並列に呼び、部品ごとの状態を返す（SCR-P-001 §6.1・§6.2）。
 *
 * - 期間の単位・範囲・担当者が変わったら 5 本とも呼び直す（BTN-P-002・003・006・007）
 * - 1 本の失敗で他を止めない（§6.3 の注記）。reload(endpoint) は失敗した 1 本だけを呼び直す（#1958 の決定 D4）
 * - 条件を続けて切り替えたとき、古い条件の応答が後から届いても表示しない（API ごとに要求の番号で捨てる）
 *
 * @param {{period: string, assigneeIds: string[], range: {from: string, to: string}|null}} filters
 */
export function useDashboardData({ period, assigneeIds, range }) {
    const [sections, setSections] = useState(initialSections);
    const requestSeq = useRef(Object.fromEntries(ENDPOINT_LIST.map((ep) => [ep, 0])));
    const mounted = useRef(true);

    useEffect(() => {
        mounted.current = true;
        return () => { mounted.current = false; };
    }, []);

    const load = useCallback(async (endpoint, filters) => {
        requestSeq.current[endpoint] += 1;
        const seq = requestSeq.current[endpoint];
        setSections((prev) => ({ ...prev, [endpoint]: { status: SECTION_STATUS.LOADING, data: null } }));

        const result = await fetchDashboardData(endpoint, filters);
        if (!mounted.current || seq !== requestSeq.current[endpoint]) return;
        setSections((prev) => ({ ...prev, [endpoint]: toSection(result) }));
    }, []);

    // 配列・オブジェクトは描画のたびに作り直されうるため、中身で比べる
    const assigneeKey = assigneeIds.join(',');
    const rangeFrom = range ? range.from : '';
    const rangeTo = range ? range.to : '';

    const buildFilters = useCallback(() => ({
        period,
        assigneeIds: assigneeKey ? assigneeKey.split(',') : [],
        range: rangeFrom ? { from: rangeFrom, to: rangeTo } : null,
    }), [period, assigneeKey, rangeFrom, rangeTo]);

    useEffect(() => {
        const filters = buildFilters();
        ENDPOINT_LIST.forEach((ep) => { load(ep, filters); });
    }, [buildFilters, load]);

    const reload = useCallback((endpoint) => {
        load(endpoint, buildFilters());
    }, [buildFilters, load]);

    return { sections, reload };
}
